import { useState } from 'react'
import { exportState, importState, clearState } from '../utils/storage'
import { Settings as SettingsIcon, Download, Upload, Trash2, Moon, Sun, Type } from 'lucide-react'

export default function Settings({ state, setState, settings, setFontSize }) {
  const [importing, setImporting] = useState(false)

  const handleExport = () => {
    try {
      exportState(state)
      alert('Data exported successfully!')
    } catch (err) {
      alert('Failed to export data: ' + err.message)
    }
  }

  const handleImport = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setImporting(true)
      const imported = await importState(file)
      
      const confirm = window.confirm(
        'Do you want to replace your current data with the imported data?\n\n' +
        'Click OK to replace, or Cancel to merge with existing data.'
      )

      if (confirm) {
        setState(imported)
      } else {
        // Merge logic would go here
        setState(imported)
      }

      alert('Data imported successfully!')
    } catch (err) {
      alert('Failed to import data: ' + err.message)
    } finally {
      setImporting(false)
      event.target.value = ''
    }
  }

  const handleClearData = () => {
    const confirm = window.confirm(
      'Are you sure you want to clear all data?\n\n' +
      'This action cannot be undone. Consider exporting your data first.'
    )

    if (confirm) {
      clearState()
      window.location.reload()
    }
  }

  return (
    <div className="space-y-6 animate-slide-in max-w-4xl mx-auto">
      <div className="flex items-center">
        <SettingsIcon className="w-8 h-8 text-primary-600 mr-3" />
        <div>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your preferences and data
          </p>
        </div>
      </div>

      {/* Appearance */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Appearance</h2>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Theme</label>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (settings.theme === 'dark') {
                    document.documentElement.classList.remove('dark')
                  }
                }}
                className={`flex-1 p-4 border-2 rounded-lg flex items-center justify-center ${
                  settings.theme === 'light'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <Sun className="w-5 h-5 mr-2" />
                Light
              </button>
              <button
                onClick={() => {
                  if (settings.theme === 'light') {
                    document.documentElement.classList.add('dark')
                  }
                }}
                className={`flex-1 p-4 border-2 rounded-lg flex items-center justify-center ${
                  settings.theme === 'dark'
                    ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <Moon className="w-5 h-5 mr-2" />
                Dark
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Font Size</label>
            <div className="flex gap-3">
              {['small', 'medium', 'large'].map((size) => (
                <button
                  key={size}
                  onClick={() => setFontSize(size)}
                  className={`flex-1 p-4 border-2 rounded-lg capitalize ${
                    settings.fontSize === size
                      ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <Type className="w-5 h-5 mx-auto mb-1" />
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">Data Management</h2>
        
        <div className="space-y-3">
          <button onClick={handleExport} className="btn-primary w-full flex items-center justify-center">
            <Download className="w-5 h-5 mr-2" />
            Export Data (Backup)
          </button>

          <label className="btn-secondary w-full flex items-center justify-center cursor-pointer">
            <Upload className="w-5 h-5 mr-2" />
            {importing ? 'Importing...' : 'Import Data (Restore)'}
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              className="hidden"
              disabled={importing}
            />
          </label>

          <button
            onClick={handleClearData}
            className="btn-danger w-full flex items-center justify-center"
          >
            <Trash2 className="w-5 h-5 mr-2" />
            Clear All Data
          </button>
        </div>

        <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm">
          <p className="text-yellow-800 dark:text-yellow-200">
            <strong>Note:</strong> Your data is stored locally in your browser.
            Export regularly to avoid data loss.
          </p>
        </div>
      </div>

      {/* About */}
      <div className="card">
        <h2 className="text-xl font-bold mb-4">About</h2>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p><strong>Version:</strong> 1.0.0</p>
          <p><strong>Schema Version:</strong> {state.meta?.schemaVersion}</p>
          <p><strong>Last Saved:</strong> {state.meta?.lastSaved ? new Date(state.meta.lastSaved).toLocaleString() : 'Never'}</p>
          <p><strong>User ID:</strong> {state.user?.id}</p>
        </div>
      </div>
    </div>
  )
}
